import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Star, Lock, Trophy, ChevronRight } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { getJourneyById, getAchievementById, getSpeciesById } from '@/data';
import { useProgress } from '@/hooks/useProgress';

export function Journey() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { progress } = useProgress();

  const journey = getJourneyById(id || '');

  if (!journey) {
    return (
      <div className="max-w-lg mx-auto px-4 py-6 text-center">
        <p className="text-slate-500">Journey not found</p>
        <Link to="/achievements" className="text-otter-600 mt-2 inline-block">
          Back to Achievements
        </Link>
      </div>
    );
  }

  const journeyProgress = progress.journeys[journey.id] || {
    currentXp: 0,
    currentLevel: 1,
    milestonesReached: [],
    lastActivity: '',
  };

  const currentLevel =
    journey.levels.findLast((l) => l.xpRequired <= journeyProgress.currentXp) ||
    journey.levels[0];
  const nextLevel = journey.levels.find(
    (l) => l.xpRequired > journeyProgress.currentXp
  );

  const xpProgress = nextLevel
    ? ((journeyProgress.currentXp - currentLevel.xpRequired) /
        (nextLevel.xpRequired - currentLevel.xpRequired)) *
      100
    : 100;

  const species = journey.speciesId ? getSpeciesById(journey.speciesId) : null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="max-w-lg mx-auto"
    >
      {/* Header */}
      <div
        className="relative h-48 flex items-end p-4"
        style={{
          background: `linear-gradient(to bottom, ${journey.theme.color}40, ${journey.theme.color}80)`,
        }}
      >
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 w-10 h-10 rounded-full bg-white/80 dark:bg-slate-800/80 backdrop-blur flex items-center justify-center"
        >
          <ArrowLeft className="w-5 h-5 text-slate-700 dark:text-slate-300" />
        </button>

        <div className="flex items-end gap-4">
          <div
            className="w-20 h-20 rounded-2xl flex items-center justify-center text-4xl bg-white/30 backdrop-blur"
          >
            {journey.type === 'species' ? '🦦' : journey.type === 'challenge' ? '⚡' : '🗺️'}
          </div>
          <div className="flex-1 pb-2">
            <h1 className="text-2xl font-display font-bold text-white text-shadow">
              {journey.name}
            </h1>
            <p className="text-white/80 text-sm">{journey.description}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-6 space-y-6">
        {/* Level Card */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-slate-500">Current Level</p>
              <p className="text-2xl font-bold" style={{ color: journey.theme.color }}>
                Lv. {currentLevel.level}
              </p>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {currentLevel.name}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-500">Total XP</p>
              <p className="text-2xl font-bold text-amber-600">
                {journeyProgress.currentXp.toLocaleString()}
              </p>
            </div>
          </div>

          {nextLevel && (
            <div>
              <div className="flex justify-between text-sm text-slate-500 mb-1">
                <span>Progress to {nextLevel.name}</span>
                <span>
                  {journeyProgress.currentXp - currentLevel.xpRequired} /{' '}
                  {nextLevel.xpRequired - currentLevel.xpRequired} XP
                </span>
              </div>
              <ProgressBar value={xpProgress} color="xp" size="md" />
            </div>
          )}

          {!nextLevel && (
            <div className="text-center py-4">
              <Trophy className="w-8 h-8 mx-auto text-amber-500 mb-2" />
              <p className="font-semibold text-slate-800 dark:text-white">
                Max Level Reached!
              </p>
            </div>
          )}
        </Card>

        {/* Species Link (if applicable) */}
        {species && (
          <Link to={`/species/${species.id}`}>
            <Card hoverable className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl bg-otter-100 dark:bg-otter-900 flex items-center justify-center text-3xl">
                🦦
              </div>
              <div className="flex-1">
                <p className="font-semibold text-slate-800 dark:text-white">
                  {species.commonName}
                </p>
                <p className="text-sm text-slate-500 italic">
                  {species.scientificName}
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400" />
            </Card>
          </Link>
        )}

        {/* Milestones Journey Map */}
        <div>
          <h2 className="text-lg font-display font-bold text-slate-800 dark:text-white mb-4">
            Journey Path
          </h2>

          <div className="relative">
            {/* Path Line */}
            <div
              className="absolute left-6 top-0 bottom-0 w-1 rounded-full bg-slate-200 dark:bg-slate-700"
              style={{ background: `linear-gradient(to bottom, ${journey.theme.color}, ${journey.theme.color}30)` }}
            />

            {/* Milestones */}
            <div className="space-y-4">
              {journey.milestones.map((milestone, index) => {
                const isReached =
                  journeyProgress.currentXp >= milestone.xpRequired;
                const isCurrent =
                  index ===
                  journey.milestones.findIndex(
                    (m) => m.xpRequired > journeyProgress.currentXp
                  ) -
                    1;
                const achievement = milestone.achievementId
                  ? getAchievementById(milestone.achievementId)
                  : null;

                return (
                  <motion.div
                    key={milestone.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="relative flex items-start gap-4"
                  >
                    {/* Milestone Marker */}
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center z-10 transition-all ${
                        isReached
                          ? 'bg-gradient-to-br shadow-lg'
                          : 'bg-slate-200 dark:bg-slate-700'
                      } ${isCurrent ? 'ring-4 ring-amber-400 ring-opacity-50' : ''}`}
                      style={
                        isReached
                          ? {
                              background: `linear-gradient(135deg, ${journey.theme.color}, ${journey.theme.color}cc)`,
                            }
                          : undefined
                      }
                    >
                      {isReached ? (
                        <Star className="w-6 h-6 text-white" />
                      ) : (
                        <Lock className="w-5 h-5 text-slate-400" />
                      )}
                    </div>

                    {/* Milestone Content */}
                    <Card
                      className={`flex-1 ${
                        isCurrent
                          ? 'border-2 border-amber-400'
                          : isReached
                          ? 'bg-gradient-to-r from-white to-slate-50 dark:from-slate-800 dark:to-slate-750'
                          : 'opacity-60'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-slate-800 dark:text-white">
                            {milestone.name}
                          </h3>
                          <p className="text-sm text-slate-500">
                            {milestone.xpRequired.toLocaleString()} XP
                          </p>
                        </div>
                        {achievement && (
                          <Badge
                            variant="achievement"
                            achievementRarity={achievement.rarity}
                          >
                            {achievement.name}
                          </Badge>
                        )}
                      </div>
                      {isCurrent && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 font-medium">
                          ← You are here!
                        </p>
                      )}
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>

        {/* All Levels */}
        <div>
          <h2 className="text-lg font-display font-bold text-slate-800 dark:text-white mb-4">
            Level Progression
          </h2>
          <div className="grid grid-cols-2 gap-3">
            {journey.levels.map((level) => {
              const isAchieved = journeyProgress.currentXp >= level.xpRequired;
              const isCurrent = level.level === currentLevel.level;

              return (
                <Card
                  key={level.level}
                  className={`text-center ${
                    isCurrent
                      ? 'border-2'
                      : isAchieved
                      ? 'bg-slate-50 dark:bg-slate-800/50'
                      : 'opacity-50'
                  }`}
                  style={
                    isCurrent
                      ? { borderColor: journey.theme.color }
                      : undefined
                  }
                >
                  <div
                    className={`w-10 h-10 rounded-full mx-auto mb-2 flex items-center justify-center ${
                      isAchieved
                        ? 'bg-gradient-to-br'
                        : 'bg-slate-200 dark:bg-slate-700'
                    }`}
                    style={
                      isAchieved
                        ? {
                            background: `linear-gradient(135deg, ${journey.theme.color}, ${journey.theme.color}aa)`,
                          }
                        : undefined
                    }
                  >
                    <span className="text-white font-bold">{level.level}</span>
                  </div>
                  <p className="font-semibold text-sm text-slate-800 dark:text-white">
                    {level.name}
                  </p>
                  <p className="text-xs text-slate-500">
                    {level.xpRequired.toLocaleString()} XP
                  </p>
                  {isCurrent && (
                    <span
                      className="inline-block mt-1 text-xs font-medium px-2 py-0.5 rounded-full"
                      style={{
                        backgroundColor: `${journey.theme.color}20`,
                        color: journey.theme.color,
                      }}
                    >
                      Current
                    </span>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
